import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import cheron from "../../../assets/chevron-pin-white.svg";

import { formatMoney } from '../../../utils/pricing';
import { recordImpression, recordClick } from '../../../utils/adEvents';

import "./styles.css";

// Advert banner — the DESTINATION_BANNER and SEARCH_BANNER slots, and the
// HOME_BANNER once Banner delegates to it.
//
// TAKES A RESOLVED ADVERT, not loose listing props. It used to be spread with
// `{...someRandomListing}` and rendered a HARDCODED `from $200pp` — the same
// fabricated price on every destination page, for every listing, whatever the
// listing actually cost. Price now comes off the resolved advert, which for a
// bound advert means off the live listing.
//
// COLOUR — derived from the ad's own image unless the advert names a panel
// colour. We sample the image's average colour and darken it just enough that
// white text clears AA (>= 4.5:1), so the panel always harmonises with the
// photo yet stays legible. If the image can't be sampled (not loaded, or
// cross-origin blocked), we fall back to the brand olive accent.
const srgbToLin = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};
const relLuminance = (r, g, b) => 0.2126 * srgbToLin(r) + 0.7152 * srgbToLin(g) + 0.0722 * srgbToLin(b);
const whiteContrast = (r, g, b) => 1.05 / (relLuminance(r, g, b) + 0.05);

const UNIT_SUFFIX = {
    PER_NIGHT: 'per night',
    PER_PERSON: 'pp',
    PER_PERSON_PER_NIGHT: 'pp per night',
    PER_GROUP: 'total',
};

const BannerDynamic = ({ ad }) => {
    const theme = useTheme();
    const fallback = theme.palette.accent.olive;
    const [sampled, setSampled] = useState(null);

    const src = ad?.imageUrl || null;
    // An explicit panel colour wins outright — an advertiser who specified
    // their brand colour did not ask for it to be averaged out of a photo.
    const override = ad?.panelColor || null;

    useEffect(() => {
        setSampled(null);
        if (!src || override) return undefined;
        let cancelled = false;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            if (cancelled) return;
            try {
                const size = 16;
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, size, size);
                const { data } = ctx.getImageData(0, 0, size, size);
                let r = 0, g = 0, b = 0, n = 0;
                for (let i = 0; i < data.length; i += 4) {
                    r += data[i];
                    g += data[i + 1];
                    b += data[i + 2];
                    n += 1;
                }
                r /= n; g /= n; b /= n;
                // Darken toward AA for white text (>= 4.5:1) without losing the hue.
                let f = 1;
                while (f > 0.1 && whiteContrast(r * f, g * f, b * f) < 4.5) f -= 0.05;
                setSampled(`rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`);
            } catch (e) {
                // tainted canvas (cross-origin) — keep the olive fallback.
            }
        };
        img.onerror = () => { };
        img.src = src;
        return () => { cancelled = true; };
    }, [src, override]);

    // A banner is on screen as soon as it renders — there is no slide to
    // become selected, so this is the moment. Deduped per session inside
    // recordImpression, which matters because this component re-renders on
    // every snapshot and every colour sample.
    useEffect(() => {
        if (ad) recordImpression(ad);
    }, [ad]);

    // Nothing sold and no house inventory: render nothing rather than an empty
    // coloured slab. Unlike the hero carousel this section pins no height, so
    // its absence costs the page nothing.
    if (!ad) return null;

    const bgColor = override || sampled || fallback;
    const location = ad.captionSubtitle;
    const price = ad.cost != null
        ? `${formatMoney(ad.cost, ad.currency || 'USD')}`
        : null;

    const panel = (
        <div className="dynamic-banner-txt-section" style={{ background: bgColor }}>
            <div className="title">
                {ad.captionTitle}
            </div>
            {ad.tagline && (
                <div className="subtitle">
                    {ad.tagline}
                </div>
            )}
            {location && (
                <div className="location">
                    <img src={cheron} alt="" />{' '}
                    <span>{location.length > 27 ? `${location.substring(0, 27)}...` : location}</span>
                </div>
            )}
            {price && (
                <div className="cost">
                    <span>from</span> {price}
                    <span> {UNIT_SUFFIX[ad.priceUnit] || 'pp'}</span>
                </div>
            )}
        </div>
    );

    const body = (
        <>
            {panel}
            {src && (
                <div
                    className="dynamic-banner-img"
                    style={{ backgroundImage: `url(${src})` }}
                    role="img"
                    aria-label={ad.imageAlt || ''}
                />
            )}
        </>
    );

    // The whole banner is the hit target when it has somewhere to go. A real
    // anchor, so hover shows the destination and middle-click opens a tab.
    if (ad.href) {
        return ad.external ? (
            <a
                className="dynamic-banner-container dynamic-banner-link"
                href={ad.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => recordClick(ad)}
            >
                {body}
            </a>
        ) : (
            <Link
                className="dynamic-banner-container dynamic-banner-link"
                to={ad.href}
                onClick={() => recordClick(ad)}
            >
                {body}
            </Link>
        );
    }

    return <div className="dynamic-banner-container">{body}</div>;
};

export default BannerDynamic;
